package com.neurobs.util

import java.time.Instant
import java.time.temporal.ChronoUnit
import io.circe.{Decoder, Encoder}

import java.time.format.DateTimeFormatter

/** Instant in global time with precision of seconds
  *
  * @param i Instant truncated to seconds
  */
class SecondsTimestamp private (i: Instant) extends Ordered[SecondsTimestamp] {
	def instant: Instant = i

	override def toString = i.toString

	/** Format YYYY-MM-DDThh:mm:ssZ */
	def isoString = {
		DateTimeFormatter.ISO_INSTANT.format(i)
	}

	def canEqual(a: Any): Boolean = a.isInstanceOf[SecondsTimestamp]

	override def equals(obj: Any): Boolean = obj match {
		case that: SecondsTimestamp => {
			that.canEqual(this) && this.instant == that.instant
		}
		case _ => false
	}

	override def hashCode(): Int = instant.hashCode()

	def withinSeconds(rhs: SecondsTimestamp, s: Long): Boolean = {
		val diff = i.getEpochSecond - rhs.instant.getEpochSecond
		(diff < s) && (diff > -s)
	}

	def secondsAgo(s: Long): SecondsTimestamp = {
		val epochSecs = (instant.getEpochSecond - s).max(0)
		SecondsTimestamp(epochSecs)
	}

	def addSeconds(s: Long): SecondsTimestamp = {
		val epochSecs = (instant.getEpochSecond + s).max(0)
		SecondsTimestamp(epochSecs)
	}

	// Implement Ordered[SecondsTimestamp] to support comparison operators
	override def compare(that: SecondsTimestamp): Int = this.instant.compareTo(that.instant)
}

object SecondsTimestamp {
	/** SecondsTimestamp from an Instant by truncating to seconds
	  *
	  * @param instant Any instant
	  * @return New SecondsTimestamp
	  */
	def apply(instant: Instant): SecondsTimestamp = new SecondsTimestamp(instant.truncatedTo(ChronoUnit.SECONDS))

	/** SecondsTimestamp from a number of seconds after epoch
	 *
	 * @param epochSeconds A number of seconds after epoch
	 * @return New SecondsTimestamp
	 */
	def apply(epochSeconds: Long): SecondsTimestamp = new SecondsTimestamp(Instant.ofEpochSecond(epochSeconds))

	def now(): SecondsTimestamp = SecondsTimestamp(Instant.now())

	// For some reason, this version of these causes compiler problems on some compilers
	//implicit val circeDecoder: Decoder[SecondsTimestamp] = Decoder.decodeLong.map(i => SecondsTimestamp(Instant.ofEpochSecond(i)))
	//implicit val circeEncoder: Encoder[SecondsTimestamp] = Encoder.encodeLong.contramap(_.instant.getEpochSecond)

	implicit val circeEncoder: Encoder[SecondsTimestamp] = new Encoder[SecondsTimestamp] {
		final def apply(a: SecondsTimestamp): io.circe.Json =
			io.circe.Json.fromLong(a.instant.getEpochSecond)
	}
	// encodeFoo: Encoder[Thing] = repl.Session$App$$anon$1@40838c90

	implicit val circeDecoder: Decoder[SecondsTimestamp] = new Decoder[SecondsTimestamp] {
		final def apply(c: io.circe.HCursor): Decoder.Result[SecondsTimestamp] =
			c.as[Long].map(i => SecondsTimestamp(Instant.ofEpochSecond(i)))
	}

	implicit val timestampOrdering: Ordering[SecondsTimestamp] = new Ordering[SecondsTimestamp] {
		override def compare(x: SecondsTimestamp, y: SecondsTimestamp): Int = x.instant.compareTo(y.instant)
	}

	case class Interval(startTime: SecondsTimestamp, endTime: SecondsTimestamp)
}

object SecondsTimestampIsoCodecs {
	import io.circe.{Decoder, Encoder}
	import scala.util.{Failure, Success, Try}
	import java.time.Instant

	implicit val secondsTimestampEncoder: Encoder[SecondsTimestamp] =
		Encoder.encodeString.contramap(_.isoString)

	implicit val secondsTimestampDecoder: Decoder[SecondsTimestamp] =
		Decoder.decodeString.emap { s =>
			Try(SecondsTimestamp(Instant.parse(s))) match {
				case Success(ts) => Right(ts)
				case Failure(e)  => Left(Option(e.getMessage).getOrElse("Invalid timestamp"))
			}
		}
}